import type { FieldPolicy } from '@apollo/client/cache';
import { relayStylePagination } from '@apollo/client/utilities';
import type { TypedTypePolicies } from './apollo-helpers';

// The plugin reads this file at codegen time and embeds it verbatim into
// the generated policies. To keep that pipeline simple, declare exports as
// `export function` only — the strip logic in `relay-pagination.ts` looks
// for that exact prefix. `export const` / `export type` / `export default`
// would leak `export` keywords into the generated file (caught by the
// plugin's "embedded helpers must not re-export" test).

/**
 * Recursively serialize a value with object keys sorted bytewise and
 * `undefined` fields dropped. Used to canonicalize keyArgs so filter
 * objects with reordered keys (`{a, b}` vs `{b, a}`) land in one cache
 * slot. Apollo's default keyArgs handler uses structural equality, but our
 * keyArgs functions return strings — so we have to canonicalize ourselves.
 */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      // Bytewise comparison: cache identity, not locale order.
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

/**
 * Wraps `relayStylePagination` so:
 *   - the cache slot is keyed by a canonical, key-order-insensitive
 *     serialization of the field's non-pagination args, and
 *   - aggregate selections (`first: 0`) get a distinct cache slot so a
 *     parallel `alias: field(first: 0)` selection in the same document
 *     can't clobber the paginated edges via the relay merge.
 */
function aggregateAwarePagination(
  keyArgs: ReadonlyArray<string>
): FieldPolicy {
  return {
    ...relayStylePagination([...keyArgs]),
    keyArgs: (args) => {
      const base = stableStringify(
        Object.fromEntries(keyArgs.map((k) => [k, args?.[k] ?? null]))
      );
      return args?.first === 0 ? `aggregate:${base}` : base;
    }
  };
}

const generatedTypePolicies: TypedTypePolicies = {
  Agent: {
    fields: {
      transcript: aggregateAwarePagination(['where']),
    }
  },
  BulkPullRequestActionCanceled: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  BulkPullRequestActionError: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  BulkPullRequestActionFinished: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  BulkPullRequestActionQueued: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  BulkPullRequestActionRunning: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  ChangelogBulkPullRequestActionCanceled: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  ChangelogBulkPullRequestActionError: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  ChangelogBulkPullRequestActionFinished: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  ChangelogBulkPullRequestActionQueued: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  ChangelogBulkPullRequestActionRunning: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  ChangelogPullRequest: {
    fields: {
      actions: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  Conversation: {
    fields: {
      messages: aggregateAwarePagination([]),
    }
  },
  DevCenter: {
    fields: {
      runs: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  MessageBoard: {
    fields: {
      messages: aggregateAwarePagination(['where']),
    }
  },
  Moddy: {
    fields: {
      conversations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  Organization: {
    fields: {
      changelog: aggregateAwarePagination(['orderBy', 'where']),
      changesets: aggregateAwarePagination(['orderBy', 'where']),
      children: aggregateAwarePagination(['orderBy', 'where']),
      participants: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationCommitCanceled: {
    fields: {
      repositories: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationCommitError: {
    fields: {
      repositories: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationCommitFinished: {
    fields: {
      repositories: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationCommitQueued: {
    fields: {
      repositories: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationCommitRunning: {
    fields: {
      repositories: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationRecipeRunCanceled: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationRecipeRunError: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationRecipeRunFinished: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationRecipeRunQueued: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationRecipeRunRunning: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationRecipeRunSyncing: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationRevision: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  OrganizationSearch: {
    fields: {
      bulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      changelogBulkPullRequestActions: aggregateAwarePagination(['orderBy', 'where']),
      commits: aggregateAwarePagination(['orderBy', 'where']),
      dataTables: aggregateAwarePagination(['orderBy', 'where']),
      repositories: aggregateAwarePagination(['orderBy', 'where']),
      visualizations: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  Query: {
    fields: {
      auditLogsDownloads: aggregateAwarePagination(['orderBy', 'where']),
      codeSearch: aggregateAwarePagination(['name', 'organizationId', 'query', 'save']),
      connectors: aggregateAwarePagination(['orderBy', 'where']),
      organizations: aggregateAwarePagination(['orderBy', 'where']),
      scmConnections: aggregateAwarePagination([]),
      users: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RecipeCategory: {
    fields: {
      categories: aggregateAwarePagination(['orderBy', 'where']),
      recipes: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RecipeDetailFinished: {
    fields: {
      recipeList: aggregateAwarePagination([]),
    }
  },
  RecipeMarketplace: {
    fields: {
      categories: aggregateAwarePagination(['orderBy', 'where']),
      installations: aggregateAwarePagination(['orderBy', 'where']),
      recipes: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositoryRecipeRunCanceled: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositoryRecipeRunError: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositoryRecipeRunFinished: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositoryRecipeRunNoLst: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositoryRecipeRunQueued: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositoryRecipeRunRunning: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositoryRevision: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  RepositorySearchResult: {
    fields: {
      results: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  User: {
    fields: {
      tokens: aggregateAwarePagination(['orderBy', 'where']),
    }
  },
  VisualizationError: {
    fields: {
      repositories: aggregateAwarePagination([]),
    }
  },
  VisualizationFinished: {
    fields: {
      repositories: aggregateAwarePagination([]),
    }
  },
  VisualizationProcessing: {
    fields: {
      repositories: aggregateAwarePagination([]),
    }
  },
};

export function mergeTypePolicies(overrides: TypedTypePolicies): TypedTypePolicies {
  const result = { ...generatedTypePolicies };
  for (const [typeName, typePolicy] of Object.entries(overrides)) {
    const key = typeName as keyof TypedTypePolicies;
    const existing = result[key];
    if (!existing) {
      result[key] = typePolicy;
    } else {
      result[key] = {
        ...existing,
        ...typePolicy,
        fields: { ...existing.fields, ...typePolicy.fields },
      };
    }
  }
  return result;
}
